import { useEffect, useRef, useState } from 'react'
import type * as Leaflet from 'leaflet'
import { boundsOf, type LatLng } from '../lib/geo'
// A CSS import has no runtime behaviour a bundler does not already resolve at
// build time — Vite extracts it into a stylesheet and the JS output ends up
// with no trace of leaflet.css at all, so this is safe even in a context with
// no DOM. Needs no ambient module declaration despite this template shipping
// no vite-env.d.ts: verified directly against `tsc -b`, not assumed.
import 'leaflet/dist/leaflet.css'

/**
 * A map, for showing `places` rows (or anything with a lat/lng) as pins.
 *
 * ── Leaflet + raster tiles, and what that rejects ─────────────────────────────
 *
 * Three shapes were on the table:
 *
 *  1. MapLibre GL (vector tiles). Better looking — crisp at any zoom, can
 *     rotate — and rejected for THIS template on cost, not looks. It ships
 *     roughly 5-6x the JS of Leaflet for behaviour this module does not ask
 *     for (markers, fit-to-bounds, a click handler need none of it). It fetches
 *     tiles and style JSON with `fetch`/XHR rather than `<img>` tags, so it
 *     needs `connect-src` opened in the CSP rather than `img-src`, plus
 *     `worker-src blob:` for the web workers it spawns to parse vector tiles —
 *     a strictly bigger hole in a policy whose entire point is to be small. And
 *     it has no usable default: MapLibre's own public demo style
 *     (demotiles.maplibre.org) is documented as being for demonstration only,
 *     so a real style needs a paid or API-keyed provider before the map shows
 *     anything at all — credentials this template cannot provision for every
 *     project generated from it.
 *
 *  2. No tiles — coastline as hand-drawn SVG, the choice ferry/RouteMap.tsx
 *     makes for its own screen. Right there, wrong here: RouteMap draws ONE
 *     fixed channel between two named points, which is genuinely a solved
 *     shape once. This component exists because `places` rows can be
 *     anywhere — a directory of businesses across an island is not a fixed
 *     geometry, and every new deployment would mean sourcing, licensing and
 *     hand-maintaining fresh coastline data for wherever that project is,
 *     which is a worse ongoing burden than a tile dependency, not a lighter
 *     one. See RouteMap.tsx's own comment for the reasoning that DOES hold
 *     for a fixed two-point route — it is not being relitigated here, just
 *     not extended to a job it was never aimed at.
 *
 *  3. Leaflet + raster tiles. Small (no runtime dependencies of its own),
 *     mature, needs only `img-src` opened in the CSP because tiles load as
 *     plain `<img>` elements, and has a real, no-key-required default
 *     (OpenStreetMap's own tile server) to evaluate and develop against. Not
 *     free of cost — see the licensing note on OSM_DEV_TILES.tileUrl below, which
 *     is the part of this decision that does not go away.
 *
 * ── Why `leaflet` is imported INSIDE the effect, not at the top of the file ──
 *
 * Effects never run outside a real browser — not during `vite build`, not in
 * scripts/prerender.mjs, not under any future SSR path. A static
 * `import 'leaflet'` would not wait for that: importing the module runs its
 * browser-feature-detection immediately, and Leaflet reads `window` doing it.
 * Anything that ever imports this component's module graph outside a browser
 * — which is exactly what prerendering is — would throw before a single line
 * of JSX ran. `import type` above is erased entirely by verbatimModuleSyntax
 * (nothing of it survives into the built output), so only the dynamic
 * `import('leaflet')` inside the effect ever touches the real package.
 *
 * ── Why the marker is an inline SVG divIcon, not Leaflet's bundled pin ───────
 *
 * Leaflet's default marker image path is computed relative to leaflet.js's
 * OWN location at publish time — correct when the file is served as-is, wrong
 * the instant a bundler moves it. That is the well-known "marker icon is a
 * broken image" bug every Leaflet-plus-Vite/webpack project meets until it
 * overrides L.Icon.Default with imported asset URLs. A divIcon sidesteps the
 * problem outright: no image request, nothing for the bundler to relocate,
 * and it is drawn in the one-stroke-weight, currentColor style Icon.tsx uses
 * rather than Leaflet's stock blue teardrop.
 */

export interface MapMarker extends LatLng {
  id: string
  label?: string
}

/**
 * A radius, drawn on the map. §19 asks for radius visualisation and Leaflet
 * already ships `L.circle`, whose radius is in METRES on the ground — so it
 * stays the right size at every zoom on its own.
 *
 * The tempting alternative, a CSS ring over the map, means reimplementing the
 * Web Mercator degrees-to-pixels relationship for the current latitude and
 * zoom. Get it slightly wrong and the ring is a few hundred metres out, looks
 * completely plausible, and is the number somebody uses to decide a structure
 * is "within range" of the land.
 */
export interface MapCircle {
  center: LatLng
  radiusKm: number
  /** Any CSS colour. Defaults to the pin colour the map already uses. */
  color?: string
}

/*
 * A module-level constant, not a `= []` default parameter.
 *
 * A fresh array literal on every render is a new identity, and `circles` is a
 * dependency of the layer effect below — so a default of `[]` would rebuild
 * every marker and every circle on every parent render. That is the same class
 * of bug the eslint hooks rule exists for, arriving through the front door.
 */
const NO_CIRCLES: MapCircle[] = []

export interface MapProps {
  markers: MapMarker[]
  /** Called with the marker's id when a marker is clicked. */
  onMarkerClick?: (id: string) => void
  /** Seeds the INITIAL view only. Re-centering a map the visitor already panned is its own bug. */
  center?: LatLng
  zoom?: number
  /** Refit the view to `markers` whenever they change. Default true. */
  fitToMarkers?: boolean
  /** Radius rings — the search radius around the land, for instance. */
  circles?: MapCircle[]
  /** A Leaflet map with no explicit height renders as a blank strip. */
  height?: number | string
  className?: string
  /**
   * REQUIRED. There is no default, on purpose — see `OSM_DEV_TILES`.
   *
   * A silent default is how a licence violation ships: the map works in
   * development, nobody thinks about it again, and production quietly serves
   * itself from donation-funded servers that say commercial access may be
   * withdrawn at any point. Making this required costs one line at the call
   * site and makes the choice visible in a code review.
   */
  tileUrl: string
  attribution: string
}

/*
 * ── The tile source, and the licence that comes with it ──────────────────────
 *
 * This is OpenStreetMap's own tile server. Read before changing the default,
 * and before shipping any project generated from this template to real
 * production traffic:
 *
 * The OSMF tile usage policy (operations.osmfoundation.org/policies/tiles)
 * says plainly that these servers are funded by donations for openstreetmap.org
 * itself, carry no SLA, and that "commercial services... should be especially
 * aware that access may be withdrawn at any point" — its own recommendation
 * for production use is a commercial provider, self-hosting, or vector tiles
 * from a provider that permits it. It also requires an identifying User-Agent
 * (a browser's default is not one), visible attribution that is never hidden
 * behind a toggle, and caching tile responses for at least 7 days.
 *
 * So: correct and sufficient for local development and for evaluating this
 * component, and NOT a production answer for a commercial Studio42 app. Before
 * a real deploy, set `tileUrl`/`attribution` to a provider whose terms cover
 * production commercial traffic (a paid plan with MapTiler, Stadia Maps,
 * Thunderforest, Geoapify or similar, or self-hosted tiles) — and update the
 * matching `img-src` entry in services/api/src/http.ts to the new host, which
 * does NOT happen automatically. See the CSP comment there for the other half
 * of this same decision.
 */
/**
 * OpenStreetMap's own tiles. **DEVELOPMENT ONLY — read the block above.**
 *
 * Exported as a named constant rather than used as a default so that reaching
 * for it is a decision somebody made and a reviewer can see. `<Map {...OSM_DEV_TILES} />`
 * in a production file is a visible thing to object to; a default nobody passed
 * is not.
 *
 * The name carries the warning into every call site, which is the only place
 * the warning ever gets read.
 */
export const OSM_DEV_TILES = {
  tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
} as const

// Vieques — where this template's first deployment actually is. Only used
// when there are no markers yet AND no `center` prop, so a first paint has
// somewhere to point the camera rather than the Gulf of Guinea (0, 0).
const FALLBACK_CENTER: LatLng = { lat: 18.13, lng: -65.42 }
const DEFAULT_ZOOM = 13

function pinIcon(L: typeof Leaflet): Leaflet.DivIcon {
  return L.divIcon({
    className: 's42-map-pin',
    html:
      '<svg width="26" height="34" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">'
      + '<path d="M12 1C6.5 1 2 5.5 2 11c0 7.5 10 20 10 20s10-12.5 10-20c0-5.5-4.5-10-10-10Z" fill="currentColor" stroke="black" stroke-opacity="0.25" stroke-width="1"/>'
      + '<circle cx="12" cy="11" r="4" fill="white"/>'
      + '</svg>',
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -30],
  })
}

export default function MapView({
  markers,
  onMarkerClick,
  center = FALLBACK_CENTER,
  zoom = DEFAULT_ZOOM,
  fitToMarkers = true,
  circles = NO_CIRCLES,
  height = 360,
  className,
  tileUrl,
  attribution,
}: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Leaflet.Map | null>(null)
  const layerRef = useRef<Leaflet.LayerGroup | null>(null)
  const leafletRef = useRef<typeof Leaflet | null>(null)
  // The map is created once, from whatever center/zoom/tileUrl/attribution
  // this component FIRST rendered with — a ref, not state, precisely so that
  // a later change to those props does not need to appear in an effect's
  // dependency list to satisfy react-hooks/exhaustive-deps. They are seeds,
  // not something this component stays reactive to.
  const initialRef = useRef({ center, zoom, tileUrl, attribution })
  // A ref, updated every render unconditionally, so the click handler
  // attached to a marker always calls the LATEST callback without that
  // callback's identity needing to be a dependency of the effect that
  // (re)builds the marker layer — an inline arrow function passed as
  // `onMarkerClick` would otherwise churn on every parent render.
  const onMarkerClickRef = useRef(onMarkerClick)
  onMarkerClickRef.current = onMarkerClick
  // Flips once the async Leaflet import + map creation below has finished,
  // so the marker-sync effect has a real, reactive signal to wait on instead
  // of guessing when the refs it reads become non-null.
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return
    let cancelled = false
    const initial = initialRef.current

    void (async () => {
      const L = await import('leaflet')
      if (cancelled || !containerRef.current) return

      const map = L.map(containerRef.current).setView([initial.center.lat, initial.center.lng], initial.zoom)
      L.tileLayer(initial.tileUrl, { attribution: initial.attribution, maxZoom: 19 }).addTo(map)

      leafletRef.current = L
      mapRef.current = map
      layerRef.current = L.layerGroup().addTo(map)
      setReady(true)
    })()

    return () => {
      cancelled = true
      mapRef.current?.remove()
      mapRef.current = null
      layerRef.current = null
      leafletRef.current = null
      setReady(false)
    }
    // Deliberately []: this creates the map ONCE. See initialRef above for why
    // center/zoom/tileUrl/attribution do not belong in this list, and the
    // effect below for why markers do not either.
  }, [])

  useEffect(() => {
    const L = leafletRef.current
    const layer = layerRef.current
    const map = mapRef.current
    if (!ready || !L || !layer || !map) return

    layer.clearLayers()

    // Circles first, so a pin inside one is never painted underneath it.
    const drawn: Leaflet.Circle[] = []
    for (const circle of circles) {
      drawn.push(
        L.circle([circle.center.lat, circle.center.lng], {
          // Leaflet takes METRES. Passing kilometres here draws a ring a
          // thousand times too small, which reads as "the radius feature is
          // broken" rather than as a unit mistake.
          radius: circle.radiusKm * 1000,
          color: circle.color ?? 'currentColor',
          weight: 1.5,
          opacity: 0.7,
          fillOpacity: 0.06,
          // Not clickable: the ring covers the pins inside it, and swallowing
          // their taps is the one thing that would make the map useless.
          interactive: false,
        }).addTo(layer),
      )
    }

    const icon = pinIcon(L)
    for (const marker of markers) {
      L.marker([marker.lat, marker.lng], { icon, title: marker.label })
        .addTo(layer)
        .on('click', () => onMarkerClickRef.current?.(marker.id))
    }

    if (fitToMarkers) {
      const box = boundsOf(markers)
      if (box) {
        map.fitBounds(
          [[box.minLat, box.minLng], [box.maxLat, box.maxLng]],
          { padding: [24, 24], maxZoom: 16 },
        )
      } else if (drawn.length) {
        // No pins but a ring: fit the ring. `getBounds()` is Leaflet's own,
        // so the metres-to-degrees conversion is theirs and not ours.
        let bounds = drawn[0]?.getBounds()
        for (const circle of drawn.slice(1)) bounds = bounds?.extend(circle.getBounds())
        if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 })
      }
    }
  }, [ready, markers, circles, fitToMarkers])

  return (
    <div
      ref={containerRef}
      className={className ? `s42-map ${className}` : 's42-map'}
      style={{ height, width: '100%' }}
      role="img"
      aria-label="Map"
    />
  )
}
